import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMap } from 'react-leaflet';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import { SearchResult } from 'leaflet-geosearch/dist/providers/provider';
import { RawResult } from 'leaflet-geosearch/dist/providers/openStreetMapProvider';

// MUI Theme
import { Clear as ClearIcon, LocationOn as LocationOnIcon } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, InputAdornment, List, ListItem, ListItemIcon, ListItemText, TextField, Typography } from '@mui/material';

// Services
import { getAllStations } from '../../../../services/station.service';

// Stations
import { Station } from '../../../../types/Station';

const useDebounce = (value: string, delay: number): string => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const GeoLocationSearch = (): React.JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<SearchResult<RawResult>[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<SearchResult<RawResult> | null>(null);
  const [showList, setShowList] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const debouncedSearchText = useDebounce(searchText, 500);

  const map = useMap();
  const searchBoxRef = useRef<HTMLDivElement>(null);

  const provider = useMemo(() => new OpenStreetMapProvider(), []);

  // Fetch stations by their IATA code
  async function fetchStationsByIataCode(iataCode: string): Promise<Station[]> {
    const filters = {
      'filters[0].fieldName': 'iataCode',
      'filters[0].fieldValue': iataCode.toUpperCase(),
      'filters[0].filterType': 'Equals',
    };
    const response = await getAllStations(filters);
    return response.data?.results ?? [];
  }

  // Converts Station to a Leaflet.SearchResult<RawResult> along with some
  // dummy data so we can plug it into the map
  const mapSearchResultToStation = (s: Station): SearchResult<RawResult> => {
    return {
      x: s.location?.longitude ?? 0,
      y: s.location?.latitude ?? 0,
      label: `${s.name} (${s.iataCode.toUpperCase()})`,
      bounds: null,
      raw: {
        place_id: '',
        licence: '',
        osm_type: 'n',
        osm_id: 0,
        boundingbox: ['', '', '', ''],
        lat: (s.location?.latitude ?? 0).toString(),
        lon: (s.location?.longitude ?? 0).toString(),
        display_name: s.name,
        class: '',
        type: '',
        importance: 1,
      },
    };
  };

  const handleSearch = useCallback(
    async (query: string): Promise<void> => {
      setLoading(true);
      setError(null);
      setSearchResults([]);
      try {
        // If query is 3 letters long, query stations.
        if (query.length == 3) {
          const stations = await fetchStationsByIataCode(query);

          const results: SearchResult<RawResult>[] = stations.map(mapSearchResultToStation);

          setSearchResults(results);
          if (!results.length) setError('No location details found');
        } else {
          // Otherwise, use OpenStreetMap search
          const results = await provider.search({ query });
          setSearchResults(results);
          if (!results.length) setError('No location details found');
        }
      } catch {
        setError('Error fetching data');
      } finally {
        setLoading(false);
      }
    },
    [provider]
  );

  const handleSelect = (location: SearchResult<RawResult>): void => {
    setShowList(false);
    setSelectedLocation(location);
    setSearchText(location.label);
    if (location) {
      const { x, y } = location;
      map.setView([y, x], 18);
    }
  };

  const handleClear = (): void => {
    setSearchText('');
    setSelectedLocation(null);
    setSearchResults([]);
    setShowList(false);
  };

  useEffect(() => {
    if (selectedLocation) {
      const { x, y } = selectedLocation;
      map.setView([y, x], 14);
    }
  }, [map, selectedLocation]);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (searchBoxRef.current && !searchBoxRef.current.contains(event.target as Node)) {
      setShowList(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (debouncedSearchText) {
      handleSearch(debouncedSearchText);
    }
  }, [debouncedSearchText, handleSearch]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchText(event.target.value);
  };

  return (
    <Box
      position='absolute'
      top='10px'
      left='50%'
      zIndex={1000}
      style={{ transform: 'translate(-50%, 0)' }}
      ref={searchBoxRef}
      width='450px'
      maxWidth='calc(100% - 100px)'
    >
      <TextField
        fullWidth
        size='small'
        value={searchText}
        onChange={handleInputChange}
        onClick={() => setShowList(true)}
        placeholder='Search location'
        variant='outlined'
        style={{ backgroundColor: '#fff', borderRadius: 5 }}
        InputProps={{
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton onClick={handleClear}>
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {loading || error ? (
        <Box mt={2} textAlign='center'>
          {loading && <CircularProgress size={24} />}
          {error && (
            <Typography color='error' variant='body2'>
              {error}
            </Typography>
          )}
        </Box>
      ) : (
        showList && (
          <List style={{ backgroundColor: '#fff', borderRadius: 5 }}>
            {searchResults.map((result, index) => (
              <ListItem button key={index} onClick={() => handleSelect(result)} style={{ cursor: 'pointer' }}>
                <ListItemIcon>
                  <LocationOnIcon color='primary' />
                </ListItemIcon>
                <ListItemText primary={result.label} />
              </ListItem>
            ))}
          </List>
        )
      )}
    </Box>
  );
};

export default GeoLocationSearch;
